// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import {
  type BendPreviewSpecifications,
  BendPreviewGeometry,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import { convertToRadians } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import type { PlacementOffset } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/buildLabelOpts';

export class Kick90Geometry extends BendPreviewGeometry {
  direction: 'right' | 'left';
  constructor(direction: 'right' | 'left') {
    super();
    this.direction = direction;
  }

  createBendDefs(dimensions: BendPreviewSpecifications) {
    const { angle, stubHeight, kickHeight, legLength, conduitSize } = dimensions;
    const conduitRadius = conduitSize / 2;
    const theta = convertToRadians(angle);
    const directionMultiplier = this.direction === 'left' ? -1 : 1;

    // The angled section forms a right triangle: the kick height is the vertical leg,
    // the hypotenuse is the actual length of conduit to cut, and bendTriangleLeg is
    // how far along the horizontal run the bend starts before the conduit rises.
    const hypotenuse = kickHeight / Math.sin(theta);
    const bendTriangleLeg = kickHeight / Math.tan(theta);

    // Leg length is marked on the side of the conduit opposite the stub — the open side of the bend.
    // For a left-direction bend the stub turns left, so the open side faces right, and vice versa.
    const legLengthLabelPlacement: PlacementOffset = {
      relativeTo: 'bendPlane',
      offset: this.direction === 'left' ? 'right' : 'left',
    };

    // After the kick bend the conduit is tilted at `angle` degrees, so left/right offsets
    // would follow that tilt. Using world-space allows us to place the label at the back of the bend,
    // which matches how a field measurement would be read.
    const stubLabelOffset: PlacementOffset = {
      relativeTo: 'world',
      offset: new THREE.Vector3(0, -conduitRadius, conduitRadius),
    };

    // This places the kick height label along the back corner of the bend,
    // which is the bottom back left for a kick right, and the bottom back right for a kick left.
    const kickHeightLabelOffset: PlacementOffset = {
      relativeTo: 'world',
      offset: new THREE.Vector3(conduitRadius * directionMultiplier, -conduitRadius, conduitRadius),
    };

    return [
      {
        runLength: legLength - bendTriangleLeg,
        thenBendBy: theta,
        endMeasuredTo: 'tubeEdge',
        startLabels: [
          {
            label: 'Leg Length',
            length: legLength,
            offsets: [legLengthLabelPlacement, { relativeTo: 'world', offset: 'down' }],
          },
        ],
        endLabels: [{ label: 'Angle', angle, radius: Math.min(1, bendTriangleLeg * 0.75), preferredDistance: 0.25 }],
      },
      {
        runLength: hypotenuse,
        thenRotateBy: (Math.PI / 2) * directionMultiplier,
        thenBendBy: Math.PI / 2,
        endLabels: [
          {
            label: 'Kick Height',
            length: kickHeight,
            isVertical: true,
            offsets: [kickHeightLabelOffset],
          },
        ],
      },
      {
        startMeasuredFrom: 'tubeEdge',
        runLength: stubHeight,
        endLabels: [
          {
            label: 'Stub Height',
            length: stubHeight,
            offsets: [stubLabelOffset],
            isReverse: true,
          },
        ],
      },
    ] as const;
  }
}
